import { useState } from "react";
import { CalendarPlus, CheckCircle2, LayoutGrid as Lists, Loader2, XCircle } from "lucide-react";
import DetailedListItem from "@/components/tasks/lists/DetailedListItem/DetailedListItem";
import SimpleTaskListItem from "@/components/tasks/lists/SimpleTaskListItem/SimpleTaskListItem";
import { DEFAULT_AREA_COLOR, resolveTaskColors } from "@/lib/listColors";
import { formatDurationShort, nowTimeString } from "@/lib/time";

export type ListTemplateRowTemplate = {
  id: number;
  title: string;
  details?: string | null;
  defaultStartTime?: string | null;
  defaultDurationMin?: number | null;
  defaultRepeatEnabled?: boolean;
  defaultRepeatIntervalMin?: number | null;
  defaultRepeatEndType?: "endTime" | "count" | null;
  defaultRepeatEndTime?: string | null;
  defaultRepeatCount?: number | null;
  barColor?: string | null;
  bgColor?: string | null;
};

const MIN_PER_DAY = 24 * 60;
const REPEAT_HARD_CAP = 200;

function timeStringToMinutesOrNull(value: string | null | undefined): number | null {
  if (!value) return null;
  const match = /^(\d{1,2}):?(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const h = parseInt(match[1]!, 10);
  const m = parseInt(match[2]!, 10);
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

function getTemplateRepeatCount(template: ListTemplateRowTemplate, startTime: string) {
  if (!template.defaultRepeatEnabled) return null;
  const start = timeStringToMinutesOrNull(startTime);
  if (start === null) return template.defaultRepeatCount ?? 1;
  const intervalMin = Math.max(1, template.defaultRepeatIntervalMin ?? 60);

  if (template.defaultRepeatEndType === "count") {
    const requested = Math.min(
      REPEAT_HARD_CAP,
      Math.max(1, template.defaultRepeatCount ?? 1),
    );
    let count = 0;
    for (let i = 0; i < requested; i++) {
      if (start + i * intervalMin >= MIN_PER_DAY) break;
      count++;
    }
    return count;
  }

  const end = timeStringToMinutesOrNull(template.defaultRepeatEndTime ?? "23:59");
  if (end === null) return 1;
  let count = 0;
  for (
    let t = start;
    t <= end && t < MIN_PER_DAY && count < REPEAT_HARD_CAP;
    t += intervalMin
  ) {
    count++;
  }
  return count;
}

export default function ListTemplateRow({
  template,
  variant = "detailed",
  listColor,
  listBgColor,
  onOpen,
  onAddToToday,
}: {
  template: ListTemplateRowTemplate;
  variant?: "simple" | "detailed";
  listColor?: string | null;
  listBgColor?: string | null;
  onOpen: () => void;
  onAddToToday: () => void | Promise<unknown>;
}) {
  const colors = resolveTaskColors({
    barColor: template.barColor,
    bgColor: template.bgColor,
    listColor: listColor ?? DEFAULT_AREA_COLOR,
    listBgColor: listBgColor ?? null,
  });
  const startTime = template.defaultStartTime ?? nowTimeString();
  const repeatCount = getTemplateRepeatCount(template, startTime);
  const schedule = [
    startTime,
    template.defaultDurationMin ? formatDurationShort(template.defaultDurationMin) : null,
    repeatCount ? `${repeatCount}x` : null,
  ].filter(Boolean).join(" | ");
  const icon = <Lists className="h-4 w-4" aria-hidden />;
  const [addFeedback, setAddFeedback] = useState<"idle" | "pending" | "success" | "error">("idle");

  async function handleAddToToday() {
    if (addFeedback === "pending") return;
    setAddFeedback("pending");
    try {
      await onAddToToday();
      setAddFeedback("success");
      window.setTimeout(() => setAddFeedback("idle"), 1800);
    } catch {
      setAddFeedback("error");
      window.setTimeout(() => setAddFeedback("idle"), 2200);
    }
  }

  const feedback = addFeedback === "success"
    ? { label: "Added to today", icon: <CheckCircle2 className="h-4 w-4" />, className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300" }
    : addFeedback === "error"
      ? { label: "Couldn’t add — try again", icon: <XCircle className="h-4 w-4" />, className: "bg-destructive/10 text-destructive" }
      : { label: "Adding to today…", icon: <Loader2 className="h-4 w-4 animate-spin" />, className: "bg-accent text-foreground" };

  if (addFeedback !== "idle") {
    return (
      <div
        className={`mx-1 my-1 flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold animate-in fade-in zoom-in-95 duration-300 ${feedback.className}`}
        role="status"
        aria-live="polite"
      >
        {feedback.icon}
        <span>{feedback.label}</span>
      </div>
    );
  }

  const action = (
    <button
      type="button"
      onClick={event => {
        event.stopPropagation();
        void handleAddToToday();
      }}
      className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-background hover:text-foreground"
      aria-label={`Add ${template.title} to today`}
      title="Add to today"
    >
      <CalendarPlus className="h-4 w-4" />
    </button>
  );

  if (variant === "simple") {
    return (
      <SimpleTaskListItem
        icon={<span style={{ color: colors.bar }}>{icon}</span>}
        title={template.title}
        meta={schedule}
        details={template.details}
        right={action}
        color={colors.ink}
        onClick={onOpen}
      />
    );
  }

  return (
    <DetailedListItem
      icon={icon}
      title={template.title}
      meta={schedule}
      details={template.details}
      right={<div className="flex h-5 items-center gap-1">{action}</div>}
      colors={colors}
      onClick={onOpen}
    />
  );
}
